import React, { useMemo } from "react";
import {
  Eye,
  SquarePen,
  Trash2,
  CheckCircle,
  XCircle,
  MinusCircle,
  Search,
  Building2,
  Briefcase,
  Filter,
} from "lucide-react";

import {
  DataTable,
  EmployeeCell,
  ColumnSelector,
  useTableConfig,
  SimpleTooltip,
  Select,
} from "../../common";
import type { TableColumn } from "../../common/Table";
import type { IUserListItem } from "../../../types/user.api.types";
import Badge from "../../common/Badge";
import {
  formatEmploymentStatusLabel,
  getEmployeeStatusVariant,
  normalizeEmploymentStatus,
} from "../../../utils/badgeVariants";
import { formatDate } from "../../../utils/timeUtils";
import type { SelectOption } from "../../common/Select";
import ExcelIcon from "../../../icons/ExcelIcon";

interface EmployeeTableSectionProps {
  employees: IUserListItem[];
  loading: boolean;
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  onView: (employee: IUserListItem) => void;
  onEdit: (employee: IUserListItem) => void;
  onDelete: (employeeId: string) => void;
  onExport: () => void;
  canManage: boolean;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  departmentOptions: SelectOption[];
  selectedDepartment: string;
  onDepartmentChange: (value: string) => void;
  employmentTypeOptions: SelectOption[];
  selectedEmploymentType: string;
  onEmploymentTypeChange: (value: string) => void;
  statusOptions: SelectOption[];
  selectedStatus: string;
  onStatusChange: (value: string) => void;
}

export const getEmployeeStatusIcon = (status?: string) => {
  const normalized = status?.trim().toLowerCase();
  switch (normalized) {
    case "active":
      return CheckCircle;
    case "inactive":
      return XCircle;
    case "terminated":
      return XCircle;
    default:
      return MinusCircle;
  }
};

const EmployeeTableSection: React.FC<EmployeeTableSectionProps> = ({
  employees,
  loading,
  currentPage,
  itemsPerPage,
  totalItems,
  onPageChange,
  onItemsPerPageChange,
  onView,
  onEdit,
  onDelete,
  onExport,
  canManage,
  searchTerm,
  onSearchChange,
  departmentOptions,
  selectedDepartment,
  onDepartmentChange,
  employmentTypeOptions,
  selectedEmploymentType,
  onEmploymentTypeChange,
  statusOptions,
  selectedStatus,
  onStatusChange,
}) => {
  const rawColumns = useMemo<TableColumn<IUserListItem>[]>(() => {
    return [
      {
        key: "employee",
        title: "Employee",
        label: "Employee",
        required: true,
        render: (_: unknown, emp: IUserListItem) => (
          <EmployeeCell
            name={emp.fullName || "—"}
            sub={emp.employeeId}
          />
        ),
      },
      {
        key: "department",
        title: "Department",
        label: "Department",
        dataIndex: "departmentName",
        render: (value: unknown) => (
          <span className="text-sm font-medium text-[var(--color-text-secondary)]">
            {(value as string) || "—"}
          </span>
        ),
      },
      {
        key: "designation",
        title: "Designation",
        label: "Designation",
        dataIndex: "designationName",
        render: (value: unknown) => (
          <span className="text-sm font-medium text-[var(--color-text-secondary)]">
            {(value as string) || "—"}
          </span>
        ),
      },
      {
        key: "manager",
        title: "Manager",
        label: "Manager",
        render: (_: unknown, emp: IUserListItem) => (
          <span className="text-sm font-medium text-[var(--color-text-secondary)]">
            {emp.reportingManagerName || "—"}
          </span>
        ),
      },
      {
        key: "joiningDate",
        title: "Joined",
        label: "Joined",
        render: (_: unknown, emp: IUserListItem) => (
          <span className="text-sm font-medium text-[var(--color-text-secondary)]">
            {emp.joiningDate ? formatDate(emp.joiningDate) : "—"}
          </span>
        ),
      },
      {
        key: "employmentType",
        title: "Employment Type",
        label: "Employment Type",
        defaultVisible: false,
        dataIndex: "employmentTypeName",
        render: (value: unknown) => (
          <span className="text-sm font-medium text-[var(--color-text-secondary)]">
            {(value as string) || "—"}
          </span>
        ),
      },
      {
        key: "email",
        title: "Work Email",
        label: "Work Email",
        defaultVisible: false,
        dataIndex: "workEmail",
        render: (value: unknown) => (
          <span className="text-sm font-medium text-[var(--color-text-secondary)]">
            {(value as string) || "—"}
          </span>
        ),
      },
      {
        key: "reportingManager",
        title: "Reporting Manager (L1)",
        label: "Reporting Manager (L1)",
        defaultVisible: false,
        render: (_: unknown, emp: IUserListItem) => (
          <span className="text-sm font-medium text-[var(--color-text-secondary)]">
            {emp.reportingManagerName || "—"}
          </span>
        ),
      },
      {
        key: "functionalManager",
        title: "Functional Manager (L2)",
        label: "Functional Manager (L2)",
        defaultVisible: false,
        render: (_: unknown, emp: IUserListItem) => (
          <span className="text-sm font-medium text-[var(--color-text-secondary)]">
            {emp.functionalManagerName || "—"}
          </span>
        ),
      },
      {
        key: "aadhar",
        title: "Aadhar Card",
        label: "Aadhar Card",
        defaultVisible: false,
        dataIndex: "aadhaarNumber",
        render: (value: unknown) => (
          <span className="text-sm font-medium text-[var(--color-text-secondary)]">
            {(value as string) || "—"}
          </span>
        ),
      },
      {
        key: "pan",
        title: "PAN Card",
        label: "PAN Card",
        defaultVisible: false,
        dataIndex: "panNumber",
        render: (value: unknown) => (
          <span className="text-sm font-medium text-[var(--color-text-secondary)]">
            {(value as string) || "—"}
          </span>
        ),
      },
      {
        key: "bankName",
        title: "Bank Name",
        label: "Bank Name",
        defaultVisible: false,
        dataIndex: "bankName",
        render: (value: unknown) => (
          <span className="text-sm font-medium text-[var(--color-text-secondary)]">
            {(value as string) || "—"}
          </span>
        ),
      },
      {
        key: "status",
        title: "Status",
        label: "Status",
        render: (_: unknown, emp: IUserListItem) => {
          const normalizedStatus = normalizeEmploymentStatus(emp.status);
          const StatusIcon = getEmployeeStatusIcon(normalizedStatus);
          return (
            <Badge
              variant={getEmployeeStatusVariant(normalizedStatus)}
              size="middle"
            >
              <StatusIcon className="w-4 h-4" />
              {formatEmploymentStatusLabel(emp.status)}
            </Badge>
          );
        },
      },
      {
        key: "actions",
        title: "Actions",
        label: "Actions",
        align: "right",
        required: true,
        render: (_: unknown, emp: IUserListItem) => (
          <div className="flex items-center justify-end gap-1 relative">
            <SimpleTooltip label="View" side="top">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  e.currentTarget.blur();
                  onView(emp);
                }}
                className="w-[34px] h-[34px] grid place-items-center border-none bg-transparent rounded-[9px] cursor-pointer transition-colors duration-150 hover:bg-[var(--color-primary-50)] focus-visible:ring-2 focus-visible:ring-[var(--color-primary-600)] focus-visible:outline-none"
              >
                <Eye className="w-4 h-4 text-[var(--color-text-tertiary)]" />
              </button>
            </SimpleTooltip>
            {canManage && (
              <>
                <SimpleTooltip label="Edit" side="top">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.currentTarget.blur();
                      onEdit(emp);
                    }}
                    className="w-[34px] h-[34px] grid place-items-center border-none bg-transparent rounded-[9px] cursor-pointer transition-colors duration-150 hover:bg-[var(--color-primary-50)] focus-visible:ring-2 focus-visible:ring-[var(--color-primary-600)] focus-visible:outline-none"
                  >
                    <SquarePen className="w-[15px] h-[15px] text-[var(--color-text-tertiary)]" />
                  </button>
                </SimpleTooltip>
                <SimpleTooltip label="Delete" side="top">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.currentTarget.blur();
                      onDelete(emp.id);
                    }}
                    className="w-[34px] h-[34px] grid place-items-center border-none bg-transparent rounded-[9px] cursor-pointer transition-colors duration-150 hover:bg-[var(--color-error-light)] focus-visible:ring-2 focus-visible:ring-[var(--color-error)] focus-visible:outline-none"
                  >
                    <Trash2 className="w-[15px] h-[15px] text-[var(--color-text-tertiary)] hover:text-[var(--color-error-dark)]" />
                  </button>
                </SimpleTooltip>
              </>
            )}
          </div>
        ),
      },
    ];
  }, [canManage, onDelete, onEdit, onView]);

  const { configuredColumns, visibleKeys, toggleVisibility, allColumns } =
    useTableConfig(rawColumns, {
      persistenceKey: "employee-management-table",
    });

  // Toolbar-only option: lets the user reset the status filter without a clear "×",
  // matching the Figma control (no clear affordance).
  const statusFilterOptions = useMemo<SelectOption[]>(
    () => [{ value: "", label: "All Statuses" }, ...statusOptions],
    [statusOptions],
  );

  return (
    <div className="bg-surface border border-[var(--color-border-secondary)] rounded-[18px] overflow-hidden shadow-[var(--shadow-sm)]">
      {/* Top Filter Bar */}
      <div className="flex items-center gap-3 flex-wrap px-[18px] sm:px-[22px] py-[14px] sm:py-[18px] border-b border-[var(--color-layout-dropdown-separator)]">
        <div className="flex items-center gap-[9px] flex-1 min-w-[200px] h-10 px-[13px] bg-[var(--color-bg-muted)] rounded-[11px]">
          <Search className="w-4 h-4 text-[var(--color-text-tertiary)] flex-none" aria-hidden="true" />
          <input
            type="search"
            aria-label="Search employees by name, ID or email"
            placeholder="Search by name, ID or email…"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="border-none bg-transparent outline-none font-inherit text-body-base text-[var(--color-text-primary)] placeholder:text-[var(--color-text-placeholder)] w-full focus-visible:outline-none"
          />
        </div>
        <div className="shrink-0">
          <Select
            variant="toolbar"
            ariaLabel="Filter by department"
            leadingIcon={<Building2 size={15} />}
            placeholder="Department"
            options={departmentOptions}
            value={selectedDepartment ?? ""}
            onChange={(value) => onDepartmentChange(String(value ?? ""))}
            clearable
            searchable
            size="sm"
          />
        </div>
        <div className="shrink-0">
          <Select
            variant="toolbar"
            ariaLabel="Filter by employment type"
            leadingIcon={<Briefcase size={15} />}
            placeholder="Employment Type"
            options={employmentTypeOptions}
            value={selectedEmploymentType ?? ""}
            onChange={(value) => onEmploymentTypeChange(String(value ?? ""))}
            clearable
            searchable
            size="sm"
          />
        </div>
        <div className="shrink-0">
          <Select
            variant="toolbar"
            ariaLabel="Filter by status"
            leadingIcon={<Filter size={15} />}
            placeholder="Status"
            options={statusFilterOptions}
            value={selectedStatus ?? ""}
            onChange={(value) => onStatusChange(String(value ?? ""))}
            clearable={false}
            size="sm"
          />
        </div>
        <div className="flex items-center gap-1 flex-none">
          <SimpleTooltip label="Export to Excel" side="bottom">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onExport();
              }}
              className="w-[34px] h-[34px] grid place-items-center border-none bg-transparent rounded-[9px] cursor-pointer transition-colors duration-150 text-[var(--color-text-tertiary)] hover:text-[var(--color-primary-600)] hover:bg-[var(--color-primary-50)] focus-visible:ring-2 focus-visible:ring-[var(--color-primary-600)] focus-visible:outline-none"
            >
              <ExcelIcon className="w-[18px] h-[18px]" />
            </button>
          </SimpleTooltip>
          <ColumnSelector
            columns={allColumns}
            visibleKeys={visibleKeys}
            onToggle={toggleVisibility}
          />
        </div>
      </div>

      {/* Reusable Table2 DataTable */}
      <DataTable<IUserListItem>
        columns={configuredColumns}
        rows={employees}
        rowKey={(emp) => emp.id}
        loading={loading}
        emptyMessage="No employees found"
        responsive="auto"
        pagination={{
          page: currentPage,
          pageSize: itemsPerPage,
          total: totalItems,
          onPageChange,
          onPageSizeChange: onItemsPerPageChange,
        }}
      />
    </div>
  );
};

export default EmployeeTableSection;
